import { create } from 'zustand';
import { detectPlatform, type InstallPlatform, isDismissed, isStandalone, recordDismissal } from '../services/installPrompt';
import { trackAction } from '../services/telemetry';

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallOutcome = 'accepted' | 'dismissed';

interface InstallState {
  /** Chromium's deferred `beforeinstallprompt`; single-use. */
  deferred: BeforeInstallPromptEvent | null;
  /** Running standalone, or installed during this session. */
  installed: boolean;
  /** The banner was dismissed (stored cooldown, or in memory when storage is unavailable). */
  dismissed: boolean;
  platform: InstallPlatform;
  /** Opens the native dialog; resolves to the player's choice, or null when there was nothing to prompt. */
  prompt: () => Promise<InstallOutcome | null>;
  dismiss: () => void;
}

const initial = { deferred: null, installed: false, dismissed: false, platform: 'other' as InstallPlatform };

// Reading `localStorage` itself can throw when storage is disabled.
function storageOf(win: Window): Storage | null {
  try {
    return win.localStorage;
  } catch {
    return null;
  }
}

let storageWin: Window | null = null;
const storage = () => storageOf(storageWin ?? window);

export const useInstall = create<InstallState>((set, get) => ({
  ...initial,
  prompt: async () => {
    const event = get().deferred;
    if (!event) return null;
    set({ deferred: null });
    try {
      await event.prompt();
      const { outcome } = await event.userChoice;
      if (outcome === 'accepted') set({ installed: true });
      else {
        recordDismissal(storage(), Date.now());
        set({ dismissed: true });
      }
      return outcome;
    } catch {
      return null;
    }
  },
  dismiss: () => {
    recordDismissal(storage(), Date.now());
    set({ dismissed: true });
  },
}));

let teardown: (() => void) | null = null;

/** Called once from main.tsx before the first render, so a prompt fired before any screen mounts is kept. Idempotent. */
export function initInstallListeners(win: Window = window): () => void {
  if (teardown) return teardown;
  storageWin = win;
  const nav = win.navigator as Navigator & { standalone?: boolean };
  const matchMedia = win.matchMedia ? win.matchMedia.bind(win) : undefined;
  useInstall.setState({
    installed: isStandalone(matchMedia, nav),
    platform: detectPlatform(nav.userAgent, nav.maxTouchPoints ?? 0),
    dismissed: isDismissed(storageOf(win), Date.now()),
  });

  const onPrompt = (e: Event) => {
    e.preventDefault();
    useInstall.setState({ deferred: e as BeforeInstallPromptEvent });
  };
  const onInstalled = () => {
    useInstall.setState({ installed: true, deferred: null });
    trackAction('app_installed');
  };
  const mql = matchMedia?.('(display-mode: standalone)');
  const onDisplayMode = (e: MediaQueryListEvent) => {
    if (e.matches) useInstall.setState({ installed: true, deferred: null });
  };

  win.addEventListener('beforeinstallprompt', onPrompt);
  win.addEventListener('appinstalled', onInstalled);
  mql?.addEventListener?.('change', onDisplayMode);
  teardown = () => {
    win.removeEventListener('beforeinstallprompt', onPrompt);
    win.removeEventListener('appinstalled', onInstalled);
    mql?.removeEventListener?.('change', onDisplayMode);
    teardown = null;
    storageWin = null;
  };
  return teardown;
}

/** Tests: detach listeners and return to the initial state. */
export function resetInstall() {
  teardown?.();
  useInstall.setState(initial);
}
